// 📊 Usage & Limits Page
// Demo page showing subscription usage and limits

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsageDashboard } from '../components/UsageDashboard';
import { UsageAlertsContainer, UsageAlertBanner } from '../components/UsageAlerts';
import { useUsageLimits } from '../hooks/useUsageLimits';
import { useSubscriptionExpiry } from '../hooks/useSubscriptionExpiry';
import { isSubscriptionValid } from '../utils/subscriptionLimits';
import { ArrowLeft, TrendingUp, AlertTriangle, Info, Clock, XCircle } from 'lucide-react';

/**
 * 📊 Usage Page Component
 */
export const UsagePage = ({ companyId }) => {
  const navigate = useNavigate();
  const { alerts, alertCounts, hasBlockedFeatures, refresh } = useUsageLimits(companyId);
  const expiryStatus = useSubscriptionExpiry(companyId, 5 * 60 * 1000); // Check every 5 minutes
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  
  useEffect(() => {
    const loadSubscriptionInfo = async () => {
      if (companyId) {
        const validity = await isSubscriptionValid(companyId);
        setSubscriptionInfo(validity);
      }
    };
    loadSubscriptionInfo();
  }, [companyId]);
  
  const handleUpgrade = () => {
    navigate('/pricing');
  };
  
  const handleDismissAlert = (alertId) => {
    // Alert will be dismissed via the component
    console.log('Dismissing alert:', alertId);
  };
  
  if (!companyId) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <p className="ml-3 text-sm text-yellow-800">
                No company selected. Please select a company to view usage.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Alert notifications */}
      <UsageAlertsContainer 
        companyId={companyId}
        onUpgrade={handleUpgrade}
      />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Usage & Limits
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Monitor your subscription usage and plan limits
                </p>
              </div>
            </div>
            
            <button
              onClick={handleUpgrade}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Subscription Expiry Warning */}
        {subscriptionInfo && !subscriptionInfo.valid && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex items-start">
              <XCircle className="w-6 h-6 text-red-500 mt-0.5" />
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-red-900">
                  Subscription Expired
                </h3>
                <p className="mt-1 text-sm text-red-800">
                  Your subscription has expired. All operations are currently blocked. Please renew your plan to continue using the service.
                </p>
                <button
                  onClick={handleUpgrade}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                >
                  Renew Plan
                </button>
              </div>
            </div>
          </div>
        )}
        
        {subscriptionInfo && subscriptionInfo.valid && subscriptionInfo.daysRemaining !== null && subscriptionInfo.daysRemaining <= 7 && (
          <div className={`mb-6 border-l-4 p-4 rounded-lg ${
            subscriptionInfo.daysRemaining <= 1 
              ? 'bg-red-50 border-red-500' 
              : subscriptionInfo.daysRemaining <= 3
              ? 'bg-orange-50 border-orange-500'
              : 'bg-yellow-50 border-yellow-500'
          }`}>
            <div className="flex items-start">
              <Clock className={`w-6 h-6 mt-0.5 ${
                subscriptionInfo.daysRemaining <= 1 
                  ? 'text-red-500' 
                  : subscriptionInfo.daysRemaining <= 3
                  ? 'text-orange-500'
                  : 'text-yellow-500'
              }`} />
              <div className="ml-3 flex-1">
                <h3 className={`text-lg font-semibold ${
                  subscriptionInfo.daysRemaining <= 1 
                    ? 'text-red-900' 
                    : subscriptionInfo.daysRemaining <= 3
                    ? 'text-orange-900'
                    : 'text-yellow-900'
                }`}>
                  {subscriptionInfo.daysRemaining <= 1 
                    ? 'Subscription Expiring Today!'
                    : `Subscription Expiring in ${subscriptionInfo.daysRemaining} Days`
                  }
                </h3>
                <p className={`mt-1 text-sm ${
                  subscriptionInfo.daysRemaining <= 1 
                    ? 'text-red-800' 
                    : subscriptionInfo.daysRemaining <= 3
                    ? 'text-orange-800'
                    : 'text-yellow-800'
                }`}>
                  {subscriptionInfo.daysRemaining <= 1
                    ? 'Your subscription expires today. Renew now to avoid service interruption.'
                    : subscriptionInfo.daysRemaining <= 3
                    ? `Your subscription will expire in ${subscriptionInfo.daysRemaining} day${subscriptionInfo.daysRemaining > 1 ? 's' : ''}. Renew now to continue using all features.`
                    : `Your subscription will expire in ${subscriptionInfo.daysRemaining} days. Consider renewing your plan soon.`
                  }
                </p>
                <button
                  onClick={handleUpgrade}
                  className={`mt-3 px-4 py-2 text-white text-sm font-medium rounded-md transition-colors ${
                    subscriptionInfo.daysRemaining <= 1 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : subscriptionInfo.daysRemaining <= 3
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  Renew Plan
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Alert Summary */}
        {alerts.length > 0 && (
          <div className="mb-6">
            <UsageAlertBanner
              alerts={alerts}
              onDismiss={handleDismissAlert}
              onUpgrade={handleUpgrade}
            />
            
            {alerts.length > 1 && (
              <button
                onClick={() => setShowAllAlerts(!showAllAlerts)}
                className="text-sm text-blue-600 hover:text-blue-700 mt-2"
              >
                {showAllAlerts ? 'Hide' : 'Show'} all {alerts.length} alerts
              </button>
            )}
            
            {showAllAlerts && (
              <div className="mt-4 space-y-2">
                {alerts.map(alert => (
                  <div key={alert.id} className="bg-white border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-gray-900">
                      {alert.message}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {alert.featureName}: {alert.current}/{alert.limit} ({alert.percentage}%)
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Stats Cards */}
        {alertCounts.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {alertCounts.blocked > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-medium">Blocked</p>
                    <p className="text-2xl font-bold text-red-900 mt-1">
                      {alertCounts.blocked}
                    </p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-xs text-red-700 mt-2">
                  Features at limit - upgrade required
                </p>
              </div>
            )}
            
            {alertCounts.critical > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Critical</p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {alertCounts.critical}
                    </p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-500" />
                </div>
                <p className="text-xs text-orange-700 mt-2">
                  Over 90% usage - approaching limit
                </p>
              </div>
            )}
            
            {alertCounts.warning > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-600 font-medium">Warning</p>
                    <p className="text-2xl font-bold text-yellow-900 mt-1">
                      {alertCounts.warning}
                    </p>
                  </div>
                  <Info className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-xs text-yellow-700 mt-2">
                  Over 80% usage - monitor closely
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* No Alerts Message */}
        {alerts.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-green-500" />
              <p className="ml-3 text-sm text-green-800">
                All features are within limits. Your usage is healthy!
              </p>
            </div>
          </div>
        )}
        
        {/* Usage Dashboard */}
        <UsageDashboard 
          companyId={companyId}
          onUpgrade={handleUpgrade}
        />
        
        {/* Help Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 flex items-center">
            <Info className="w-4 h-4 mr-2" />
            Understanding Usage Limits
          </h3>
          <div className="mt-3 text-sm text-blue-800 space-y-2">
            <p>
              • <strong>Warning (80%):</strong> You're approaching your plan limit. Consider upgrading soon.
            </p>
            <p>
              • <strong>Critical (90%):</strong> You're very close to your limit. Upgrade to avoid interruption.
            </p>
            <p>
              • <strong>Blocked (100%):</strong> You've reached your limit. Upgrade now to continue using this feature.
            </p>
          </div>
          <button
            onClick={handleUpgrade}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            View Plans & Pricing
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsagePage;

